import type { KarmaNumerology } from "@/lib/karma/types";
import { computeArchetypeProfile } from "@/lib/karma/archetypes";
import { getShopBundleForCode } from "@/lib/karma/shop";

export type KarmaInsightTone = "strength" | "growth" | "practice";

export type KarmaInsight = {
  id: string;
  title: string;
  body: string;
  tone: KarmaInsightTone;
};

const DIGIT_SHORT: Record<number, string> = {
  1: "Lider",
  2: "Uyumlu",
  3: "Yaratıcı",
  4: "Düzenli",
  5: "Özgür ruhlu",
  6: "Şifacı",
  7: "Derin",
  8: "Güçlü",
  9: "Bilge",
};

export function getDigitShortLabel(digit: number) {
  const v = Math.trunc(Number(digit));
  if (!Number.isFinite(v) || v < 1 || v > 9) return "";
  return DIGIT_SHORT[v] ?? "";
}

const DIGIT_KEYWORDS: Record<number, { title: string; strengths: string; shadows: string; practice: string }> = {
  1: {
    title: "Başlatan",
    strengths: "net karar, cesaret, liderlik",
    shadows: "acelecilik, yalnızlaşma, kontrol",
    practice: "Tek hedef seç, ilk adımı 10 dakikada başlat.",
  },
  2: {
    title: "Birleştiren",
    strengths: "empati, diplomasi, sezgi",
    shadows: "kararsızlık, alınganlık, erteleme",
    practice: "Bir konuyu açıkça sor; varsayım yerine netlik seç.",
  },
  3: {
    title: "İfade Eden",
    strengths: "yaratıcılık, iletişim, neşe",
    shadows: "dağınıklık, ciddiyetsizlik, savrulma",
    practice: "Kısa üretim: 15 dakika yaz/çiz/konuş—sonuç değil süreç.",
  },
  4: {
    title: "İnşa Eden",
    strengths: "düzen, disiplin, güven",
    shadows: "katılık, eleştirellik, aşırı çalışma",
    practice: "Bugün 3 küçük adım yaz; sadece birini yapmak bile yeter.",
  },
  5: {
    title: "Özgürleştiren",
    strengths: "esneklik, değişim, merak",
    shadows: "sabırsızlık, bağımlılıklar, tutarsızlık",
    practice: "Tek bir alışkanlığı küçükçe değiştir; bedenine hareket ekle.",
  },
  6: {
    title: "Şifalandıran",
    strengths: "sorumluluk, şefkat, koruyuculuk",
    shadows: "fazla yük alma, memnun etme, suçluluk",
    practice: "Sınır cümlesi: ‘Şu an buna evet diyemem’.",
  },
  7: {
    title: "Derinleşen",
    strengths: "analiz, içgörü, ruhsallık",
    shadows: "mesafe, kuşku, içe kapanma",
    practice: "Bir soruya tek cevap: ‘Bu bana ne öğretiyor?’",
  },
  8: {
    title: "Yöneten",
    strengths: "bolluk, hedef, güç",
    shadows: "sertlik, güç savaşı, hırs",
    practice: "Başarı kriterini yaz; adil paylaşım ve şeffaflık seç.",
  },
  9: {
    title: "Tamamlayan",
    strengths: "bilgelik, şefkat, geniş bakış",
    shadows: "aşırı fedakârlık, dağılma, vedalaşamama",
    practice: "Yarım kalan tek şeyi kapat; sadeleştir ve bırak.",
  },
};

function safeDigit(n: number) {
  const v = Math.trunc(Number(n));
  return Number.isFinite(v) && v >= 1 && v <= 9 ? v : 1;
}

function digitInfo(d: number) {
  return DIGIT_KEYWORDS[safeDigit(d)] ?? DIGIT_KEYWORDS[1];
}

function buildNumberInsight(args: {
  id: string;
  label: string;
  digit: number;
  angle: "purpose" | "heart" | "talent" | "first" | "rhythm";
}): KarmaInsight {
  const { id, label, digit, angle } = args;
  const info = digitInfo(digit);

  const prefix =
    angle === "purpose"
      ? "Ana tema"
      : angle === "heart"
        ? "Kalpte ihtiyaç"
        : angle === "talent"
          ? "Doğal yetenek"
          : angle === "first"
            ? "İlk izlenim"
            : "Günlük ritim";

  const body =
    angle === "purpose"
      ? `${prefix}: ${info.title}. Güç: ${info.strengths}. Denge: ${info.shadows}.`
      : angle === "heart"
        ? `${prefix}: ${info.title}. Seni besleyen: ${info.strengths}. Denge: ${info.shadows}.`
        : angle === "talent"
          ? `${prefix}: ${info.title}. Üretim tarzı: ${info.strengths}. Dikkat: ${info.shadows}.`
          : angle === "first"
            ? `${prefix}: ${info.title}. İnsanlar seni ${info.strengths} üzerinden hissedebilir; ${info.shadows} tetiklenebilir.`
            : `${prefix}: ${info.title}. Hızın: ${info.strengths}. Zorlanma: ${info.shadows}.`;

  return {
    id,
    title: `${label}: ${safeDigit(digit)}`,
    body,
    tone: angle === "purpose" || angle === "talent" ? "strength" : "growth",
  };
}

export function getProfileInsights(n: KarmaNumerology): KarmaInsight[] {
  const insights: KarmaInsight[] = [];

  const { archetype, elementLabel, elementHint } = computeArchetypeProfile(n);
  insights.push({
    id: "archetype",
    title: `Arketip: ${archetype.name}`,
    body: `Tema: ${archetype.headline} Güç: ${archetype.strengths}. Denge: ${archetype.shadows}. Pratik: ${archetype.practice}.`,
    tone: archetype.tone,
  });

  insights.push(
    buildNumberInsight({
      id: "destiny",
      label: "Kader",
      digit: n.destinyNumber,
      angle: "purpose",
    }),
  );

  insights.push(
    buildNumberInsight({
      id: "heart",
      label: "Kalp arzusu",
      digit: n.heartDesireNumber,
      angle: "heart",
    }),
  );

  insights.push(
    buildNumberInsight({
      id: "expression",
      label: "İfade",
      digit: n.expressionNumber,
      angle: "talent",
    }),
  );

  insights.push(
    buildNumberInsight({
      id: "personality",
      label: "Kişilik",
      digit: n.personalityNumber,
      angle: "first",
    }),
  );

  insights.push(
    buildNumberInsight({
      id: "birthday",
      label: "Doğum günü",
      digit: n.birthDayNumber,
      angle: "rhythm",
    }),
  );

  if (n.karmicCodes.length) {
    const codes = Array.from(new Set(n.karmicCodes.map((c) => safeDigit(c))));

    const lines = codes.map((code) => {
      const bundle = getShopBundleForCode(code);
      const practice = bundle.items[0];
      return `Karmik Kod ${code}: ${bundle.headline}. Pratik: ${practice.title} — ${practice.description}`;
    });

    insights.push({
      id: "karmic_codes",
      title: `Karmik Kodlar: ${codes.join(", ")}`,
      body: lines.join("\n"),
      tone: "practice",
    });
  }

  return insights;
}
